import { MODELS } from '../constants';
import * as geminiService from './geminiService';
import * as openaiService from './openaiService';
import * as claudeService from './claudeService';
import { RubricTransformContext, RubricTransformResponse } from '../types';

/**
 * Unified AI service that routes requests to the appropriate provider
 * based on the selected model
 */

function getServiceForModel(modelId: string) {
    const model = MODELS[modelId];
    if (!model) {
        throw new Error(`Unknown model: ${modelId}`);
    }

    if (modelId.startsWith('gemini-')) {
        return geminiService;
    } else if (modelId.startsWith('gpt-') || modelId.startsWith('openai-')) {
        return openaiService;
    } else if (modelId.startsWith('claude-')) {
        return claudeService;
    } else {
        // Default to Gemini for backwards compatibility
        return geminiService;
    }
}

/**
 * Analyze pasted image using the appropriate AI service
 */
export const analyzePastedImage = async (imageBase64: string, modelId: string = 'gemini-2.5-flash') => {
    const service = getServiceForModel(modelId);
    return await service.analyzePastedImage(imageBase64);
};

/**
 * Transcribe image using the appropriate AI service
 */
export const transcribeImage = async (imageBase64: string, modelId: string = 'gemini-2.5-flash') => {
    const service = getServiceForModel(modelId);
    return await service.transcribeImage(imageBase64);
};

/**
 * Mark essay using the appropriate AI service
 */
export const markEssay = async (
    transcribedText: string,
    systemInstructions: string,
    markingRubric: string,
    assessmentQuestion: string,
    yearLevel: string,
    modelConfig: any,
    exemplars: any[] = [],
    essayInputType: string,
    assessmentType: string | null = null,
    examCondition: string | null = null,
    minWordCount: number | null = null,
    maxWordCount: number | null = null,
    onStatusUpdate?: (message: string) => void,
    retryAttempt: number = 0
) => {
    const service = getServiceForModel(modelConfig.id);
    return await service.markEssay(
        transcribedText,
        systemInstructions,
        markingRubric,
        assessmentQuestion,
        yearLevel,
        modelConfig,
        exemplars,
        essayInputType,
        assessmentType,
        examCondition,
        minWordCount,
        maxWordCount,
        onStatusUpdate,
        retryAttempt
    );
};

/**
 * Generate feedback using the appropriate AI service
 */
export const generateFeedback = async (
    transcribedText: string,
    systemInstructions: string,
    markingRubric: string,
    assessmentQuestion: string,
    finalConsolidatedResults: any,
    finalOverallScore: number,
    finalOverallBand: string,
    allMarkingRunsData: any,
    yearLevel: string,
    modelConfig: any,
    consolidatedAuditData: any
) => {
    const service = getServiceForModel(modelConfig.id);
    return await service.generateFeedback(
        transcribedText,
        systemInstructions,
        markingRubric,
        assessmentQuestion,
        finalConsolidatedResults,
        finalOverallScore,
        finalOverallBand,
        allMarkingRunsData,
        yearLevel,
        modelConfig,
        consolidatedAuditData
    );
};

/**
 * Run meta analysis using the appropriate AI service
 * Currently only supported by Gemini models
 */
export const runMetaAnalysis = async (
    essayText: string,
    rubric: string,
    assessmentQuestion: string,
    yearLevel: string,
    conflictingRunsData: any[],
    modelConfig: any
) => {
    // For now, only Gemini supports meta-analysis
    if (modelConfig.id.startsWith('gemini-')) {
        return await geminiService.runMetaAnalysis(essayText, rubric, assessmentQuestion, yearLevel, conflictingRunsData);
    } else {
        console.warn(`Model ${modelConfig.id} doesn't support runMetaAnalysis, falling back to Gemini`);
        return await geminiService.runMetaAnalysis(essayText, rubric, assessmentQuestion, yearLevel, conflictingRunsData);
    }
};

/**
 * Generate consolidated justifications using the appropriate AI service
 * Currently only supported by Gemini models
 */
export const generateConsolidatedJustifications = async (
    transcribedText: string,
    markingRubric: string,
    assessmentQuestion: string,
    consolidatedCriteria: any,
    allMarkingRunsData: any,
    yearLevel: string,
    modelConfig: any
) => {
    // For now, only Gemini supports justification generation
    if (modelConfig.id.startsWith('gemini-')) {
        return await geminiService.generateConsolidatedJustifications(
            transcribedText,
            markingRubric,
            assessmentQuestion,
            consolidatedCriteria,
            allMarkingRunsData,
            yearLevel,
            modelConfig
        );
    } else {
        console.warn(`Model ${modelConfig.id} doesn't support generateConsolidatedJustifications, falling back to Gemini`);
        // Use default Gemini model config when falling back
        const defaultGeminiConfig = MODELS['gemini-3.1-pro-preview'];
        return await geminiService.generateConsolidatedJustifications(
            transcribedText,
            markingRubric,
            assessmentQuestion,
            consolidatedCriteria,
            allMarkingRunsData,
            yearLevel,
            defaultGeminiConfig
        );
    }
};

/**
 * Explain marking variance using the appropriate AI service
 * Currently only supported by Gemini models
 */
export const explainMarkingVariance = async (
    essayData: any,
    assessment: any,
    teacherFeedback: string,
    modelConfig: any,
    selectedModelForComparison?: string
) => {
    // For now, only Gemini supports variance explanation
    if (modelConfig.id.startsWith('gemini-')) {
        return await geminiService.explainMarkingVariance(essayData, assessment, teacherFeedback, modelConfig, selectedModelForComparison);
    } else {
        console.warn(`Model ${modelConfig.id} doesn't support explainMarkingVariance, falling back to Gemini`);
        // Use default Gemini model config when falling back
        const defaultGeminiConfig = MODELS['gemini-3.1-pro-preview'];
        return await geminiService.explainMarkingVariance(essayData, assessment, teacherFeedback, defaultGeminiConfig, selectedModelForComparison);
    }
};

export const transformRubricDocument = async (
    rubricText: string,
    context: RubricTransformContext,
    modelId: string = 'gemini-2.5-flash'
): Promise<RubricTransformResponse> => {
    if (!rubricText || !rubricText.trim()) {
        throw new Error('No rubric content was provided for transformation.');
    }

    const service = getServiceForModel(modelId);
    if (service?.transformRubricDocument) {
        return await service.transformRubricDocument(rubricText, context, modelId);
    }

    if (service !== geminiService && typeof geminiService.transformRubricDocument === 'function') {
        return await geminiService.transformRubricDocument(rubricText, context, modelId);
    }

    throw new Error('Rubric transformation is not supported for the selected model.');
};
